import * as React from 'react';
import { Image, StyleSheet, View } from 'react-native';

import color from '../../../style/color';
import { getRegion, Region } from '../../../lib/auth/region';

// Enables the shared Instruction* components' "version B" styling (plain black step numbers, no inline
// pills/icons) for the vertical variant only, leaving the control (animation) group untouched.
export const VerticalInstructionsVariantContext = React.createContext(false);

// Illustrations embed localized Safari UI labels, so they are region-specific (FR vs US/GB). PNG rather than
// SVG because the cards use a backdrop blur SVG cannot render. Step 1 (the address bar) only shows
// "app.joko.com", so a single card is shared across regions and reused by the activation/authorization screens.
const ASSET_BASE = '/assets/images/illustrations/mobile-safari-extension-installation';

type RegionalIllustration = {
    aspectRatio: number;
    sources: Record<Region, string>;
};

function localizedSources(frFile: string, usFile: string = frFile): Record<Region, string> {
    return {
        [Region.FR]: `${ASSET_BASE}/${frFile}`,
        [Region.US]: `${ASSET_BASE}/${usFile}`,
        [Region.GB]: `${ASSET_BASE}/${usFile}`,
    };
}

const addressBarIllustration: RegionalIllustration = {
    aspectRatio: 930 / 416,
    sources: localizedSources('version-b-instruction-activation-1.png'),
};

export const verticalActivationIllustrations: Record<1 | 2 | 3, RegionalIllustration> = {
    1: addressBarIllustration,
    2: {
        aspectRatio: 931 / 418,
        sources: localizedSources(
            'version-b-instruction-activation-2.png',
            'version-b-instruction-activation-2-us.png'
        ),
    },
    3: {
        aspectRatio: 931 / 385,
        sources: localizedSources(
            'version-b-instruction-activation-3.png',
            'version-b-instruction-activation-3-us.png'
        ),
    },
};

export const verticalAuthorizationFallbackIllustrations: Record<1 | 2 | 3 | 4, RegionalIllustration> = {
    1: addressBarIllustration,
    2: {
        aspectRatio: 931 / 517,
        sources: localizedSources(
            'version-b-instruction-authorization-2.png',
            'version-b-instruction-authorization-2-us.png'
        ),
    },
    3: {
        aspectRatio: 922 / 570,
        sources: localizedSources(
            'version-b-instruction-authorization-3.png',
            'version-b-instruction-authorization-3-us.png'
        ),
    },
    4: {
        aspectRatio: 922 / 570,
        sources: localizedSources(
            'version-b-instruction-authorization-4.png',
            'version-b-instruction-authorization-4-us.png'
        ),
    },
};

export function VerticalInstructions({ children }: { children: React.ReactNode }) {
    const steps = React.Children.toArray(children).filter(Boolean);
    return (
        <VerticalInstructionsVariantContext.Provider value={true}>
            <View style={styles.containerVerticalInstructions}>
                {steps.map((step, index) => (
                    <React.Fragment key={index}>
                        {index > 0 ? <View style={styles.stepDivider} /> : null}
                        {step}
                    </React.Fragment>
                ))}
            </View>
        </VerticalInstructionsVariantContext.Provider>
    );
}

export function VerticalInstructionStep({
    illustration,
    children,
    hasFullTopPadding = false,
}: {
    illustration: RegionalIllustration;
    children: React.ReactNode;
    // Authorization steps 3 & 4 keep the original (larger) top padding; their bottom padding is still reduced.
    hasFullTopPadding?: boolean;
}) {
    const uri = illustration.sources[getRegion()];
    // react-native-web 0.14 lacks `aspectRatio` support, so a width:100% <Image> with no explicit height
    // collapses to 0. Reserve height via the padding-bottom percentage trick, then absolutely fill it.
    const paddingBottom = `${100 / illustration.aspectRatio}%`;
    return (
        <View style={styles.containerVerticalStep}>
            {children}
            <View style={[styles.illustrationCard, hasFullTopPadding && styles.illustrationCardFullTopPadding]}>
                <View style={[styles.illustrationFrame, { paddingBottom }]}>
                    <Image source={{ uri }} style={styles.illustration} resizeMode="contain" />
                </View>
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    containerVerticalInstructions: {
        alignSelf: 'stretch',
        gap: 16,
        paddingHorizontal: 16,
        paddingVertical: 16,
        borderWidth: 1,
        borderRadius: 12,
        borderColor: color.mercury,
    },
    // Sits inside the container padding so it doesn't reach the rounded border; `gap` spaces it evenly.
    stepDivider: {
        height: 1,
        backgroundColor: color.mercury,
    },
    containerVerticalStep: {
        alignSelf: 'stretch',
        gap: 8,
    },
    illustrationCard: {
        alignSelf: 'stretch',
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 8,
        backgroundColor: color.white,
    },
    illustrationCardFullTopPadding: {
        paddingTop: 12,
    },
    illustrationFrame: {
        width: '100%',
    },
    illustration: {
        ...StyleSheet.absoluteFillObject,
        width: '100%',
        height: '100%',
    },
});
